/**
 * "We couldn't load this, try again" — the recoverable-fetch-failure banner.
 *
 * Distinct from a refusal: nothing is wrong with what the visitor asked for, so
 * it offers a retry rather than a reason. Amber, not red — red is for something
 * they have to fix.
 *
 * Amber, not red — red (`SectionErrorState`, next door) is for something the
 * visitor has to fix, and it deliberately offers no retry.
 *
 * This markup existed as four character-for-character copies before it was
 * extracted. It lives in `packages/ui` rather than in one app because the fifth
 * would have been mobile's: `WaiverBookingNotice` re-typed the class list
 * verbatim while this file sat in `web-portal-2`, which is how a shared banner
 * becomes two diverging banners the first time a palette moves.
 *
 * Static markup — no state, no effects (NativeWebFirst).
 *
 * @module components/feedback/RetryBanner
 */

export interface RetryBannerProps {
  /** What failed, in the visitor's terms. A sentence, not an error code. */
  message: string;
  onRetry: () => void;
  testId: string;
  /** Defaults to "Retry". Override when the action reads better as something else. */
  retryLabel?: string;
}

export function RetryBanner({ message, onRetry, testId, retryLabel = 'Retry' }: RetryBannerProps) {
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800"
      role="alert"
      data-testid={testId}
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        className="font-semibold underline text-interactive:text-yellow-900"
      >
        {retryLabel}
      </button>
    </div>
  );
}
